import { useEffect, useState } from 'react';

/**
 * Returns a debounced copy of `value` that only updates after `delayMs` has
 * elapsed without further changes.
 *
 * Useful for search inputs: the raw input value can update on every keystroke
 * (keeping typing responsive) while the debounced value — which drives the
 * expensive downstream work like filtering or fetching — only settles once the
 * user pauses. Each change resets the timer, so rapid typing collapses into a
 * single update.
 */
export function useDebouncedValue<T>(value: T, delayMs = 200): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
